import { ApiQuoteResponse, ApiCoverageDetail, ApiFlight, DateInformation, ApiPackage, ITotalablePremium } from '@bhtp-client-api/base';
import { AcknowledgementService, CoverageCartViewModel, Mapper } from '@bhtp-client-web/component';
import { NameValuePair } from './name-value-pair.model';
import { Coverage } from './../coverages/coverage.model';
import { PurchaseData, BillingInformation, Traveler, Flight, Country, TripDetails } from './../';
import { Constants } from './../../settings/constants';
import { ApiPolicyEditResponse, ApiEditTraveler, ApiEditFlight, RefundedPaymentSummary } from '@bhtp-client-api/edit';
import { TransactionSummary } from './transaction-summary.model';

import * as moment from 'moment';
import { CurrencyPipe } from "@angular/common";

export class ReceiptData {
    public isCustomSession: boolean;
    public policyNumber: string;
    public customForm: Array<NameValuePair>;

    public destinationCountry: Country;
    public destinationState: string;
    public destinationCity: string;

    public depositDate: string;
    public departureDate: string;
    public returnDate: string;

    public effectiveDate: string;
    public expirationDate: string;

    public primaryTraveler: Traveler;
    public additionalTravelers: Array<Traveler>;

    public standardCoverages: Array<Coverage>;
    public extraCoverages: Array<Coverage>;
    public optionalCoverages: Array<Coverage>;

    public purchasedPackage: ApiPackage;

    public flights: Array<Flight>;

    // billing information
    public billingName: string;
    public cardType: string;
    public last4: string;

    /**
     * The new amount that was paid/refunded.
     * 0 = no premium change
     * > 0 = new payment made
     * < 0 = refund was made.
     */
    public newAmountDue: number;

    /**
     * The new total premium amount. includes the prior premium + any
     * increase or decrease in premiums during edit.
     */
    public totalCost: number;

    public hasFees: boolean;

    public quoteResponse: ApiQuoteResponse;

    // Postponed Flight data
    public futureFlightsExpected: boolean;
    public overrideRequiresFlights: boolean;

    public transactions: TransactionSummary[] = [];

    public static createFromPurchaseData(data: PurchaseData, policy: ApiQuoteResponse, billingInfo: BillingInformation, purchasedPackage: ApiPackage = null): ReceiptData {
        let receipt: ReceiptData = new ReceiptData();

        if (data) {
            receipt.quoteResponse = policy;

            receipt.primaryTraveler = data.primaryTraveler;
            receipt.additionalTravelers = data.additionalTravelers;
            receipt.destinationCountry = data.destinationCountry;
            receipt.destinationState = data.destinationState;
            receipt.destinationCity = data.destinationCity;

            if (data.flights) {
                receipt.flights = [];
                data.flights.forEach((f: Flight) => {
                    if (f.departureAirport && f.arrivalAirport && f.airline &&
                        f.flightNumber && f.flightDate) {
                        receipt.flights.push(f);
                    }
                });
            }

            // Set Flight Postponed Data
            receipt.futureFlightsExpected = policy.futureFlightsExpected;
            receipt.overrideRequiresFlights = policy.overrideRequiresFlights;

            // set acknowledgements on flights
            if (receipt.flights && data.acknowledgements) {
                receipt.flights.forEach(f => {
                    f.acknowledgements = AcknowledgementService.getAcknowledgementsForFlight(f, data.acknowledgements);
                });
            }
        }

        if (policy) {
            if (policy.coverages) {
                AcknowledgementService.setAcknowledgementsOnCoverages(policy.coverages.included, data.acknowledgements);
                AcknowledgementService.setAcknowledgementsOnCoverages(policy.coverages.optional, data.acknowledgements);

                receipt.standardCoverages = Coverage.createCoverages(policy.coverages.included, ['standard'], true);
                receipt.extraCoverages = Coverage.createCoverages(policy.coverages.included, ['extra', 'extra upgrade'], true);
                receipt.optionalCoverages = Coverage.createCoverages(policy.coverages.optional, ['upgrade'], true);
            }

            receipt.policyNumber = policy.policyNumber ? policy.policyNumber.toString() : null;
            receipt.totalCost = ReceiptData.getTotalPrice(policy);
            receipt.newAmountDue = receipt.totalCost;

            receipt.hasFees = policy.totalFees && policy.totalFees > 0;

            receipt.depositDate = parseDate(policy.depositDates);
            receipt.departureDate = parseDate(policy.departureDates);
            receipt.returnDate = parseDate(policy.returnDates);
            receipt.effectiveDate = parseDate(policy.effectiveDates);
            receipt.expirationDate = parseDate(policy.expirationDates);
        }

        if (billingInfo) {
            receipt.billingName = billingInfo.name;
            receipt.cardType = billingInfo.cardType;
            receipt.last4 = billingInfo.last4;

            receipt.transactions = ReceiptData.buildPaymentTransactions(receipt.newAmountDue, purchasedPackage.name, billingInfo);
        }

        receipt.purchasedPackage = purchasedPackage;

        return receipt;

        function parseDate(dateInfo: DateInformation): string {
            if (dateInfo) {
                let date: moment.Moment = moment(dateInfo.localized.dateString, Constants.DATE_PARSE_FORMAT);
                return `${date.format(Constants.DATE_DISPLAY_FORMAT)} ${dateInfo.localTimeZoneAbbreviation}`;
            }
        }
    }

    public static getAllTravelers(data: ReceiptData): Array<Traveler> {
        let allTravelers: Array<Traveler> = new Array<Traveler>();

        if (data.primaryTraveler) {
            allTravelers.push(data.primaryTraveler);
        }

        if (data.additionalTravelers && data.additionalTravelers.length > 0) {
            allTravelers.concat(data.additionalTravelers);
        }

        return allTravelers;
    }

    public static createFromEditData(data: ApiPolicyEditResponse, editPurchaseResponse: ApiPolicyEditResponse, billingInfo: BillingInformation, tripDetails: TripDetails, purchasedPackage: ApiPackage = null): ReceiptData {
        let receipt: ReceiptData = new ReceiptData();
        receipt.additionalTravelers = [];

        if (data && data.policy) {
            // Set Flight Postponed Data
            receipt.futureFlightsExpected = data.policy.futureFlightsExpected;
            receipt.overrideRequiresFlights = data.policy.overrideRequiresFlights;
        }

        if (tripDetails) {
            receipt.destinationCountry = new Country();
            receipt.destinationCountry.name = tripDetails.destination;
            receipt.destinationCity = tripDetails.destinationCity;
            receipt.destinationState = tripDetails.destinationState;
        }

        if (editPurchaseResponse) {
            receipt.quoteResponse = editPurchaseResponse;

            if (editPurchaseResponse.travelers) {
                receipt.primaryTraveler = Traveler.fromApiTraveler(editPurchaseResponse.travelers.find(t => t.isPrimary));

                editPurchaseResponse.travelers.filter(t => !t.isPrimary).forEach(t => {
                    receipt.additionalTravelers.push(Traveler.fromApiTraveler(t));
                });
            }

            if (editPurchaseResponse.coverages) {
                receipt.standardCoverages = Coverage.createCoverages(editPurchaseResponse.coverages.included, ['standard'], true);
                receipt.extraCoverages = Coverage.createCoverages(editPurchaseResponse.coverages.included, ['extra', 'extra upgrade'], true);
                receipt.optionalCoverages = Coverage.createCoverages(editPurchaseResponse.coverages.optional, ['upgrade'], true);
            }

            if (editPurchaseResponse.flights) {
                receipt.flights = [];
                editPurchaseResponse.flights.forEach((flight: ApiFlight) => {

                    if (flight.departureAirportCode && flight.arrivalAirportCode && flight.airlineCode &&
                        flight.flightNumber && flight.departureDate) {
                        let receiptFlight: Flight = new Flight();

                        receiptFlight = Flight.fromApiFlight(flight);
                        receiptFlight.flightDate = Mapper.fromParseFormatToDisplayFormat(receiptFlight.flightDate);

                        receipt.flights.push(receiptFlight);
                    }
                });
            }

            receipt.policyNumber = editPurchaseResponse.policyNumber ? editPurchaseResponse.policyNumber.toString() : null;
            receipt.totalCost = ReceiptData.getTotalPrice(editPurchaseResponse);
            receipt.newAmountDue = editPurchaseResponse.newAmountDue;
            receipt.hasFees = editPurchaseResponse.totalFees && editPurchaseResponse.totalFees > 0;

            if (editPurchaseResponse.departureDates) {
                let departureDate: moment.Moment = moment(editPurchaseResponse.departureDates.localized.dateString, Constants.DATE_PARSE_FORMAT);
                receipt.departureDate = `${departureDate.format(Constants.DATE_DISPLAY_FORMAT)} ${editPurchaseResponse.departureDates.localTimeZoneAbbreviation}`;
            }

            if (editPurchaseResponse.returnDates) {
                let returnDate: moment.Moment = moment(editPurchaseResponse.returnDates.localized.dateString, Constants.DATE_PARSE_FORMAT);
                receipt.returnDate = `${returnDate.format(Constants.DATE_DISPLAY_FORMAT)} ${editPurchaseResponse.returnDates.localTimeZoneAbbreviation}`;
            }
        }

        if (editPurchaseResponse.newAmountDue < 0) {
            receipt.transactions = ReceiptData.buildRefundTransactions(editPurchaseResponse.refundedPaymentInfo);
        }
        else if (editPurchaseResponse.newAmountDue > 0) {
            receipt.transactions = ReceiptData.buildPaymentTransactions(editPurchaseResponse.newAmountDue, purchasedPackage.name, billingInfo);
        }

        receipt.purchasedPackage = purchasedPackage;

        return receipt;
    }

    public static buildPaymentTransactions(totalPremium: number, productName: string, cardInfo: BillingInformation): TransactionSummary[] {
        const formattedPurchaseAmount: string = new CurrencyPipe('us').transform(totalPremium, 'USD', true, '1.2-2');
        const purchaseTransaction: TransactionSummary = new TransactionSummary();
        purchaseTransaction.paymentInfo = `${ cardInfo.cardType } ending in ${ cardInfo.last4 }`;
        purchaseTransaction.productName = productName;
        purchaseTransaction.amountFormattedForDisplay = formattedPurchaseAmount;

        return [ purchaseTransaction ];
    }

    public static buildRefundTransactions(refundInfo: RefundedPaymentSummary[]): TransactionSummary[] {
        const transactionsToReturn: TransactionSummary[] = [];

        if (!refundInfo || refundInfo.length < 1) {
            return transactionsToReturn;
        }

        for (let i: number = 0; i < refundInfo.length; i++) {
            const refund: RefundedPaymentSummary = refundInfo[i];
            const formattedPurchaseAmount: string = new CurrencyPipe('us').transform(Math.abs(refund.amountRefunded), 'USD', true, '1.2-2');
            const refundTransaction: TransactionSummary = new TransactionSummary();
            refundTransaction.paymentInfo = `Card ending in ${ refund.customerPaymentIdentifier }`;
            refundTransaction.productName = refund.productName;
            refundTransaction.amountFormattedForDisplay = `-${ formattedPurchaseAmount }`;

            transactionsToReturn.push(
                refundTransaction
            );
        }

        return transactionsToReturn;
    }

    private static getTotalPrice(policy: ITotalablePremium): number {
        return policy.baseQuoteAmount + policy.optionalCoveragesQuoteAmount + policy.totalFees;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/shared/models/receipt/receipt-data.model.ts